import React, { useState, useEffect } from 'react';
import './App.css';
// Logo als Modul importieren (Datei muss unter src/assets/logo.jpeg liegen)
import logo from './assets/logo.jpeg';

// Use relative URL so it works both locally and in production
const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Admin Login Component
const AdminLogin = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('adminToken', data.access_token);
        onLogin(data.access_token);
      } else {
        setError('Ungültige Anmeldedaten');
      }
    } catch (err) {
      setError('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg max-w-md w-full mx-4">
        <h2 className="text-2xl font-bold mb-6 text-center">Admin Login</h2>
        {error && <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>}
        <form onSubmit={handleLogin}>
          <div className="mb-4">
            <input
              type="text"
              placeholder="Benutzername"
              value={credentials.username}
              onChange={(e) => setCredentials({...credentials, username: e.target.value})}
              className="w-full p-3 border rounded-lg focus:outline-none focus:border-red-700"
              required
            />
          </div>
          <div className="mb-6">
            <input
              type="password"
              placeholder="Passwort"
              value={credentials.password}
              onChange={(e) => setCredentials({...credentials, password: e.target.value})}
              className="w-full p-3 border rounded-lg focus:outline-none focus:border-red-700"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-red-700 text-white py-3 rounded-lg hover:bg-red-800 disabled:opacity-50"
          >
            {loading ? 'Anmelden...' : 'Anmelden'}
          </button>
        </form>
        {/* <div className="mt-4 text-sm text-gray-600 text-center">
          Demo: admin / rugby2025
        </div> */}
      </div>
    </div>
  );
};

// Admin Panel Component
const AdminPanel = ({ token, onLogout }) => {
  const [activeTab, setActiveTab] = useState('news');
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [boardMembers, setBoardMembers] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [showGalleryPicker, setShowGalleryPicker] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const authHeaders = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  const fetchNews = async () => {
    try {
      const response = await fetch(`${API}/news`);
      const data = await response.json();
      setNews(data);
    } catch (err) {
      console.error('Error fetching news:', err);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${API}/events`);
      const data = await response.json();
      setEvents(data);
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  };

  const fetchGalleryImages = async () => {
    try {
      const response = await fetch(`${API}/gallery`);
      if (response.ok) {
        const data = await response.json();
        setGalleryImages(data);
      } else {
        console.error('Error fetching gallery: Response not ok');
        setGalleryImages([]);
      }
    } catch (err) {
      console.error('Error fetching gallery:', err);
      setGalleryImages([]);
    }
  };

  const fetchBoardMembers = async () => {
    try {
      const response = await fetch(`${API}/board`);
      if (response.ok) {
        const data = await response.json();
        setBoardMembers(data);
      } else {
        console.error('Error fetching board members: Response not ok');
        setBoardMembers([]);
      }
    } catch (err) {
      console.error('Error fetching board members:', err);
      setBoardMembers([]);
    }
  };

  useEffect(() => {
    fetchNews();
    fetchEvents();
    fetchGalleryImages();
    fetchBoardMembers();
  }, []);

  const handleSaveNews = async (e) => {
    e.preventDefault();
    try {
      const url = editingItem ? `${API}/admin/news/${editingItem.id}` : `${API}/admin/news`;
      const method = editingItem ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: authHeaders,
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        fetchNews();
        setEditingItem(null);
        setFormData({});
      }
    } catch (err) {
      console.error('Error saving news:', err);
    }
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    try {
      const url = editingItem ? `${API}/admin/events/${editingItem.id}` : `${API}/admin/events`;
      const method = editingItem ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: authHeaders,
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        fetchEvents();
        setEditingItem(null);
        setFormData({});
      }
    } catch (err) {
      console.error('Error saving event:', err);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Wirklich löschen?')) return;
    
    try {
      const response = await fetch(`${API}/admin/${type}/${id}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (response.ok) {
        if (type === 'news') fetchNews();
        if (type === 'events') fetchEvents();
        if (type === 'gallery') fetchGalleryImages();
        if (type === 'board') fetchBoardMembers();
      }
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  const handleSaveBoardMember = async (e) => {
    e.preventDefault();
    try {
      const url = editingItem ? `${API}/admin/board/${editingItem.id}` : `${API}/admin/board`;
      const method = editingItem ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: authHeaders,
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        fetchBoardMembers();
        setEditingItem(null);
        setFormData({});
      }
    } catch (err) {
      console.error('Error saving board member:', err);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await fetch(`${API}/admin/gallery/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });

      if (response.ok) {
        fetchGalleryImages();
      } else {
        alert('Fehler beim Hochladen des Bildes');
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      alert('Fehler beim Hochladen des Bildes');
    } finally {
      setUploadingImage(false);
    }
  };

  const selectGalleryImage = (image) => {
    setFormData({...formData, image_url: `${BACKEND_URL}${image.url}`});
    setShowGalleryPicker(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
      <div className="bg-red-700 text-white p-4">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Ingolstadt Baboons - Admin Panel</h1>
          <button onClick={onLogout} className="bg-red-800 px-4 py-2 rounded hover:bg-red-900">
            Abmelden
          </button>
        </div>
      </div>

      <div className="flex">
        <div className="w-64 bg-gray-100 min-h-screen p-4">
          <nav className="space-y-2">
            <button
              onClick={() => setActiveTab('news')}
              className={`w-full text-left p-3 rounded ${activeTab === 'news' ? 'bg-red-700 text-white' : 'hover:bg-gray-200'}`}
            >
              News verwalten
            </button>
            <button
              onClick={() => setActiveTab('events')}
              className={`w-full text-left p-3 rounded ${activeTab === 'events' ? 'bg-red-700 text-white' : 'hover:bg-gray-200'}`}
            >
              Events verwalten
            </button>
            <button
              onClick={() => setActiveTab('board')}
              className={`w-full text-left p-3 rounded ${activeTab === 'board' ? 'bg-red-700 text-white' : 'hover:bg-gray-200'}`}
            >
              Vorstand verwalten
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`w-full text-left p-3 rounded ${activeTab === 'gallery' ? 'bg-red-700 text-white' : 'hover:bg-gray-200'}`}
            >
              Galerie verwalten
            </button>
          </nav>
        </div>

        <div className="flex-1 p-6">
          {activeTab === 'news' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">News verwalten</h2>
                <button
                  onClick={() => {
                    setEditingItem(null);
                    setFormData({ title: '', content: '', image_url: '' });
                  }}
                  className="bg-red-700 text-white px-4 py-2 rounded hover:bg-red-800"
                >
                  Neue News hinzufügen
                </button>
              </div>

              {(editingItem !== null || Object.keys(formData).length > 0) && (
                <div className="bg-gray-50 p-6 rounded-lg mb-6">
                  <h3 className="text-lg font-bold mb-4">
                    {editingItem ? 'News bearbeiten' : 'Neue News erstellen'}
                  </h3>
                  <form onSubmit={handleSaveNews}>
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-2">Titel</label>
                      <input
                        type="text"
                        value={formData.title || ''}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        className="w-full p-3 border rounded-lg"
                        required
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-2">Inhalt</label>
                      <textarea
                        value={formData.content || ''}
                        onChange={(e) => setFormData({...formData, content: e.target.value})}
                        className="w-full p-3 border rounded-lg h-32"
                        required
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-2">Bild (aus Galerie)</label>
                      <div className="flex items-center space-x-4">
                        {formData.image_url && (
                          <div className="relative">
                            <img 
                              src={formData.image_url} 
                              alt="Vorschau" 
                              className="w-24 h-24 object-cover rounded-lg border"
                            />
                            <button
                              type="button"
                              onClick={() => setFormData({...formData, image_url: ''})}
                              className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 text-sm hover:bg-red-700"
                            >
                              ×
                            </button>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => setShowGalleryPicker(true)}
                          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                        >
                          {formData.image_url ? 'Bild ändern' : 'Bild aus Galerie wählen'}
                        </button>
                      </div>
                    </div>
                    <div className="space-x-2">
                      <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
                        Speichern
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingItem(null);
                          setFormData({});
                        }}
                        className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                      >
                        Abbrechen
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="space-y-4">
                {news.map(article => (
                  <div key={article.id} className="bg-white border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold">{article.title}</h3>
                        <p className="text-gray-600 mb-2">{formatDate(article.date)}</p>
                        <p className="text-gray-700">{article.content.substring(0, 100)}...</p>
                      </div>
                      <div className="space-x-2">
                        <button
                          onClick={() => {
                            setEditingItem(article);
                            setFormData({
                              title: article.title,
                              content: article.content,
                              image_url: article.image_url || ''
                            });
                          }}
                          className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                        >
                          Bearbeiten
                        </button>
                        <button
                          onClick={() => handleDelete('news', article.id)}
                          className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                        >
                          Löschen
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'events' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Events verwalten</h2>
                <button
                  onClick={() => {
                    setEditingItem(null);
                    setFormData({ 
                      title: '', 
                      description: '', 
                      event_type: 'training',
                      date: '',
                      location: '',
                      opponent: '',
                      is_home: true
                    });
                  }}
                  className="bg-red-700 text-white px-4 py-2 rounded hover:bg-red-800"
                >
                  Neues Event hinzufügen
                </button>
              </div>

              {(editingItem !== null || Object.keys(formData).length > 0) && (
                <div className="bg-gray-50 p-6 rounded-lg mb-6">
                  <h3 className="text-lg font-bold mb-4">
                    {editingItem ? 'Event bearbeiten' : 'Neues Event erstellen'}
                  </h3>
                  <form onSubmit={handleSaveEvent}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Titel</label>
                        <input
                          type="text"
                          value={formData.title || ''}
                          onChange={(e) => setFormData({...formData, title: e.target.value})}
                          className="w-full p-3 border rounded-lg"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Event Typ</label>
                        <select
                          value={formData.event_type || 'training'}
                          onChange={(e) => setFormData({...formData, event_type: e.target.value})}
                          className="w-full p-3 border rounded-lg"
                        >
                          <option value="training">Training</option>
                          <option value="match">Spiel</option>
                          <option value="tournament">Turnier</option>
                          <option value="meeting">Versammlung</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Datum & Zeit</label>
                        <input
                          type="datetime-local"
                          value={formData.date || ''}
                          onChange={(e) => setFormData({...formData, date: e.target.value})}
                          className="w-full p-3 border rounded-lg"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Ort</label>
                        <input
                          type="text"
                          value={formData.location || ''}
                          onChange={(e) => setFormData({...formData, location: e.target.value})}
                          className="w-full p-3 border rounded-lg"
                          required
                        />
                      </div>
                    </div>
                    
                    {formData.event_type === 'match' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Gegner</label>
                          <input
                            type="text"
                            value={formData.opponent || ''}
                            onChange={(e) => setFormData({...formData, opponent: e.target.value})}
                            className="w-full p-3 border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Heim/Auswärts</label>
                          <select
                            value={formData.is_home ? 'true' : 'false'}
                            onChange={(e) => setFormData({...formData, is_home: e.target.value === 'true'})}
                            className="w-full p-3 border rounded-lg"
                          >
                            <option value="true">Heimspiel</option>
                            <option value="false">Auswärtsspiel</option>
                          </select>
                        </div>
                      </div>
                    )}

                    <div className="mt-4">
                      <label className="block text-sm font-medium mb-2">Beschreibung</label>
                      <textarea
                        value={formData.description || ''}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="w-full p-3 border rounded-lg h-24"
                        required
                      />
                    </div>

                    <div className="space-x-2 mt-4">
                      <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
                        Speichern
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingItem(null);
                          setFormData({});
                        }}
                        className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                      >
                        Abbrechen
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="space-y-4">
                {events.map(event => (
                  <div key={event.id} className="bg-white border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-lg font-bold">{event.title}</h3>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            event.event_type === 'match' ? 'bg-red-100 text-red-700' :
                            event.event_type === 'training' ? 'bg-blue-100 text-blue-700' :
                            event.event_type === 'tournament' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {event.event_type === 'match' ? 'Spiel' :
                             event.event_type === 'training' ? 'Training' :
                             event.event_type === 'tournament' ? 'Turnier' : 'Versammlung'}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-1">{formatDate(event.date)}</p>
                        <p className="text-gray-600 mb-1">{event.location}</p>
                        {event.opponent && (
                          <p className="text-gray-600 mb-1">
                            vs {event.opponent} ({event.is_home ? 'Heim' : 'Auswärts'})
                          </p>
                        )}
                        <p className="text-gray-700">{event.description}</p>
                      </div>
                      <div className="space-x-2">
                        <button
                          onClick={() => {
                            setEditingItem(event);
                            setFormData({
                              title: event.title,
                              description: event.description,
                              event_type: event.event_type,
                              date: new Date(event.date).toISOString().slice(0, -1),
                              location: event.location,
                              opponent: event.opponent || '',
                              is_home: event.is_home
                            });
                          }}
                          className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                        >
                          Bearbeiten
                        </button>
                        <button
                          onClick={() => handleDelete('events', event.id)}
                          className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                        >
                          Löschen
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Galerie verwalten</h2>
                <label className="bg-red-700 text-white px-4 py-2 rounded hover:bg-red-800 cursor-pointer">
                  {uploadingImage ? 'Wird hochgeladen...' : 'Bild hochladen'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploadingImage}
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {galleryImages.map(image => (
                  <div key={image.id} className="relative group">
                    <img
                      src={`${BACKEND_URL}${image.url}`}
                      alt={image.original_name}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                      <button
                        onClick={() => handleDelete('gallery', image.id)}
                        className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                      >
                        Löschen
                      </button>
                    </div>
                    <p className="text-xs text-gray-600 mt-1 truncate">{image.original_name}</p>
                  </div>
                ))}
              </div>

              {galleryImages.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <p>Noch keine Bilder in der Galerie.</p>
                  <p className="text-sm mt-2">Laden Sie Bilder hoch, um sie in News verwenden zu können.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'board' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Vorstand verwalten</h2>
                <button
                  onClick={() => {
                    setEditingItem(null);
                    setFormData({ name: '', position: '', description: '', image_url: '', order: 0 });
                  }}
                  className="bg-red-700 text-white px-4 py-2 rounded hover:bg-red-800"
                >
                  Neues Vorstandsmitglied hinzufügen
                </button>
              </div>

              {/* Board Member Form */}
              {(editingItem !== null || formData.name !== undefined) && (
                <form onSubmit={handleSaveBoardMember} className="bg-gray-50 p-6 rounded-lg mb-6">
                  <h3 className="text-lg font-semibold mb-4">
                    {editingItem ? 'Vorstandsmitglied bearbeiten' : 'Neues Vorstandsmitglied'}
                  </h3>
                  <div className="grid gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Name</label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full p-2 border rounded focus:outline-none focus:border-red-700"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Position/Funktion</label>
                      <input
                        type="text"
                        value={formData.position || ''}
                        onChange={(e) => setFormData({...formData, position: e.target.value})}
                        className="w-full p-2 border rounded focus:outline-none focus:border-red-700"
                        placeholder="z.B. 1. Vorsitzender, Kassenwart, Jugendwart..."
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Beschreibung</label>
                      <textarea
                        value={formData.description || ''}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        rows={3}
                        className="w-full p-2 border rounded focus:outline-none focus:border-red-700"
                        placeholder="Kurze Beschreibung zur Person..."
                        required
                      ></textarea>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Reihenfolge (niedrigere Zahl = weiter oben)</label>
                      <input
                        type="number"
                        value={formData.order || 0}
                        onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                        className="w-full p-2 border rounded focus:outline-none focus:border-red-700"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Bild</label>
                      <div className="flex items-center gap-4">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            const uploadData = new FormData();
                            uploadData.append('file', file);
                            try {
                              const response = await fetch(`${API}/admin/board/upload`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}` },
                                body: uploadData
                              });
                              if (response.ok) {
                                const data = await response.json();
                                setFormData({...formData, image_url: `${BACKEND_URL}${data.url}`});
                              } else {
                                alert('Fehler beim Hochladen des Bildes');
                              }
                            } catch (err) {
                              console.error('Error uploading board image:', err);
                              alert('Fehler beim Hochladen des Bildes');
                            }
                          }}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
                        />
                        {formData.image_url && (
                          <div className="flex items-center gap-2">
                            <img src={formData.image_url} alt="Preview" className="h-16 w-16 object-contain bg-gray-100 rounded" />
                            <button
                              type="button"
                              onClick={() => setFormData({...formData, image_url: ''})}
                              className="text-red-600 hover:text-red-800"
                            >
                              ✕
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-4 mt-4">
                    <button
                      type="submit"
                      className="bg-red-700 text-white px-6 py-2 rounded hover:bg-red-800"
                    >
                      Speichern
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingItem(null);
                        setFormData({});
                      }}
                      className="bg-gray-300 px-6 py-2 rounded hover:bg-gray-400"
                    >
                      Abbrechen
                    </button>
                  </div>
                </form>
              )}

              {/* Board Members List */}
              <div className="grid gap-4">
                {boardMembers.map(member => (
                  <div key={member.id} className="bg-white border rounded-lg p-4 flex items-center gap-4">
                    {member.image_url ? (
                      <img
                        src={member.image_url}
                        alt={member.name}
                        className="w-20 h-20 object-contain bg-gray-100 rounded-lg"
                      />
                    ) : (
                      <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center text-gray-400">
                        <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                    <div className="flex-1">
                      <h4 className="font-bold text-lg">{member.name}</h4>
                      <p className="text-red-700 font-medium">{member.position}</p>
                      <p className="text-gray-600 text-sm mt-1">{member.description}</p>
                      <p className="text-gray-400 text-xs mt-1">Reihenfolge: {member.order}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingItem(member);
                          setFormData({
                            name: member.name,
                            position: member.position,
                            description: member.description,
                            image_url: member.image_url || '',
                            order: member.order || 0
                          });
                        }}
                        className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => handleDelete('board', member.id)}
                        className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                      >
                        Löschen
                      </button>
                    </div>
                  </div>
                ))}

                {boardMembers.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <p>Noch keine Vorstandsmitglieder angelegt.</p>
                    <p className="text-sm mt-2">Fügen Sie Vorstandsmitglieder hinzu, um sie auf der Website anzuzeigen.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Gallery Picker Modal */}
      {showGalleryPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">Bild aus Galerie wählen</h3>
              <button
                onClick={() => setShowGalleryPicker(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {galleryImages.length > 0 ? (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
                  {galleryImages.map(image => (
                    <div
                      key={image.id}
                      onClick={() => selectGalleryImage(image)}
                      className="cursor-pointer hover:opacity-75 transition-opacity border-2 border-transparent hover:border-red-700 rounded-lg overflow-hidden"
                    >
                      <img
                        src={`${BACKEND_URL}${image.url}`}
                        alt={image.original_name}
                        className="w-full h-32 object-cover"
                      />
                      <p className="text-xs text-gray-600 p-2 truncate">{image.original_name}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <p>Noch keine Bilder in der Galerie.</p>
                  <p className="text-sm mt-2">Laden Sie zuerst Bilder im Galerie-Tab hoch.</p>
                </div>
              )}
            </div>
            <div className="p-4 border-t">
              <button
                onClick={() => setShowGalleryPicker(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminToken, setAdminToken] = useState(localStorage.getItem('adminToken'));
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [boardMembers, setBoardMembers] = useState([]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleAdminLogin = (token) => {
    setAdminToken(token);
    setShowAdminLogin(false);
    setShowAdminPanel(true);
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('adminToken');
    setAdminToken(null);
    setShowAdminPanel(false);
  };

  // Fetch news, events and board members
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [newsResponse, eventsResponse, boardResponse] = await Promise.all([
          fetch(`${API}/news`),
          fetch(`${API}/events`),
          fetch(`${API}/board`)
        ]);
        
        if (newsResponse.ok) {
          const newsData = await newsResponse.json();
          setNews(newsData.slice(0, 3)); // Show only latest 3 news
        }
        
        if (eventsResponse.ok) {
          const eventsData = await eventsResponse.json();
          setEvents(eventsData);
        }

        if (boardResponse.ok) {
          const boardData = await boardResponse.json();
          setBoardMembers(boardData);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    if (!showAdminPanel) {
      fetchData();
    }
  }, [showAdminPanel]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatEventDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (showAdminPanel && adminToken) {
    return <AdminPanel token={adminToken} onLogout={handleAdminLogout} />;
  }

  return (
    <div className="min-h-screen bg-white">
      {showAdminLogin && <AdminLogin onLogin={handleAdminLogin} />}
      
      {/* Navigation */}
      <nav className="bg-white shadow-lg fixed w-full z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {/* Logo vor Überschrift (per Import, damit Bundler es korrekt verarbeitet) */}
                <img src={logo} alt="Vereinslogo" className="h-10 w-auto mr-3 inline-block align-middle" loading="lazy" />
                <h1 className="text-2xl font-bold text-red-700 inline-block align-middle">Rubgy Ingolstadt</h1>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className="text-gray-700 hover:text-red-700 transition duration-300">Home</a>
              <a href="#about" className="text-gray-700 hover:text-red-700 transition duration-300">Über Uns</a>
              <a href="#board" className="text-gray-700 hover:text-red-700 transition duration-300">Vorstand</a>
              <a href="#teams" className="text-gray-700 hover:text-red-700 transition duration-300">Teams</a>
              <a href="#training" className="text-gray-700 hover:text-red-700 transition duration-300">Training</a>
              <a href="#news" className="text-gray-700 hover:text-red-700 transition duration-300">News</a>
              <a href="#events" className="text-gray-700 hover:text-red-700 transition duration-300">Events</a>
              <a href="#contact" className="text-gray-700 hover:text-red-700 transition duration-300">Kontakt</a>
              <a href="/impressum.html" className="text-gray-700 hover:text-red-700 transition duration-300">Impressum</a>
              {/* <button className="bg-red-700 text-white px-6 py-2 rounded-lg hover:bg-red-800 transition duration-300">
                Mitglied werden
              </button> */}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button onClick={toggleMenu} className="text-gray-700 hover:text-red-700">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white shadow-lg">
              <a href="#home" className="block px-3 py-2 text-gray-700 hover:text-red-700">Home</a>
              <a href="#about" className="block px-3 py-2 text-gray-700 hover:text-red-700">Über Uns</a>
              <a href="#board" className="block px-3 py-2 text-gray-700 hover:text-red-700">Vorstand</a>
              <a href="#teams" className="block px-3 py-2 text-gray-700 hover:text-red-700">Teams</a>
              <a href="#training" className="block px-3 py-2 text-gray-700 hover:text-red-700">Training</a>
              <a href="#news" className="block px-3 py-2 text-gray-700 hover:text-red-700">News</a>
              <a href="#events" className="block px-3 py-2 text-gray-700 hover:text-red-700">Events</a>
              <a href="#contact" className="block px-3 py-2 text-gray-700 hover:text-red-700">Kontakt</a>
              <a href="/impressum.html" className="block px-3 py-2 text-gray-700 hover:text-red-700">Impressum</a>
              {/* <button className="w-full text-left px-3 py-2 bg-red-700 text-white rounded">
                Mitglied werden
              </button> */}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center bg-gray-900">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/team-photo.jpg)'
          }}
        ></div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            Ingolstadt Baboons
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Tradition, Leidenschaft und Gemeinschaft seit 1999 - Dein Rugby Club im Herzen Ingolstadts
          </p>
          <div className="space-x-4">
            {/* <button className="bg-red-700 text-white px-8 py-3 rounded-lg text-lg hover:bg-red-800 transition duration-300">
              Jetzt Mitglied werden
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg text-lg hover:bg-white hover:text-gray-900 transition duration-300">
              Training besuchen
            </button> */}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Über die Ingolstadt Baboons</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Seit fast drei Jahrzehnten stehen wir für Rugby in Ingolstadt - vereint durch Respekt, Integrität und Leidenschaft
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="/Team_2010.jpg"
                alt="Baboons Ingolstadt Team"
                className="rounded-lg shadow-lg w-full"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Unsere Geschichte</h3>
              <p className="text-lg text-gray-600 mb-6">
                Die Ingolstadt Baboons wurden 1999 von einer Gruppe leidenschaftlicher Rugby-Enthusiasten gegründet. 
                Was als kleine Gemeinschaft begann, ist heute eine etablierte Abteilung.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Wir bieten Rugby für alle Altersgruppen und Spielstärken. 
                Unser Vereinsgelände liegt Zentral im Herzen Ingolstadts und bietet optimale 
                Trainingsbedingungen.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Ab nächster Saison pflegen wir eine enge Spielgemeinschaft mit dem Würzburger Rugby Klub. Diese Kooperation ermöglicht es, unsere Kräfte zu bündeln und gemeinsam an Turnieren und Ligaspielen teilzunehmen.
              </p>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-3xl font-bold text-red-700">60+</div>
                  <div className="text-gray-600">Mitglieder</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-red-700">26</div>
                  <div className="text-gray-600">Spieler</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-red-700">26</div>
                  <div className="text-gray-600">Jahre</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Board Members Section */}
      <section id="board" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Unser Vorstand</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Die Menschen, die unseren Verein führen und gestalten
            </p>
          </div>
          
          {boardMembers.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {boardMembers.map((member) => (
                <div key={member.id} className="bg-gray-50 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition duration-300">
                  {member.image_url && (
                    <div className="bg-gray-100 flex items-center justify-center">
                      <img 
                        src={member.image_url} 
                        alt={member.name} 
                        className="w-full h-auto max-h-80 object-contain"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                    <p className="text-red-700 font-semibold mb-3">{member.position}</p>
                    {member.description && (
                      <p className="text-gray-600 text-sm">{member.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500">
              <p>Vorstandsmitglieder werden bald hier angezeigt.</p>
            </div>
          )}
        </div>
      </section>

      {/* Teams Section */}
      <section id="teams" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Unsere Teams</h2>
            <p className="text-xl text-gray-600">
              Rugby für jeden - von Anfängern bis zu erfahrenen Spielern
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4 justify-center">
            {/* Herren 1. Mannschaft */}
            <div className="bg-gray-50 rounded-lg p-8 flex flex-col items-center hover:shadow-lg transition duration-300">
              <div className="w-16 h-16 bg-red-700 rounded-full flex items-center justify-center mb-4">
                <span className="text-white text-2xl font-bold">H</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Erwachsene</h3>
              <p className="text-gray-600 mb-4 text-center">
                Unsere Herren spielen in der Landesliga und kämpfen jede Saison um den Aufstieg.
              </p>
              <ul className="text-left text-gray-600 space-y-2">
                <li>• Training:</li>
                <li>→ Dienstag: 19:00 - 21:00 Uhr</li>
                <li>→ Donnerstag: 19:00 - 21:00 Uhr</li>
                <li>• Liga: Landesliga</li>
                <li>• Saison: September - Juni</li>
                <li>• Jugend ab U16</li>
                <li>• Frauen sind auch herzlich Willkommen</li>
              </ul>
            </div>
            {/* Jugend */}
            <div className="bg-gray-50 rounded-lg p-8 flex flex-col items-center hover:shadow-lg transition duration-300">
              <div className="w-16 h-16 bg-red-700 rounded-full flex items-center justify-center mb-4">
                <span className="text-white text-2xl font-bold">J</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Kinder & Jugend</h3>
              <p className="text-gray-600 mb-4 text-center">
                Von U6 bis U14
              </p>
              <ul className="text-left text-gray-600 space-y-2">
                <li>• Training:</li>
                <li>→ Donnerstag: 18:00 - 19:00 Uhr</li>
                <li>→ Sonntag: 10:30 - 12:00 Uhr</li>
                <li>• Saison: September - Juni</li>
              </ul>
            </div>
            {/* Kinder
            <div className="bg-gray-50 rounded-lg p-8 flex flex-col items-center hover:shadow-lg transition duration-300">
              <div className="w-16 h-16 bg-red-700 rounded-full flex items-center justify-center mb-4">
                <span className="text-white text-2xl font-bold">J</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Kinder</h3>
              <p className="text-gray-600 mb-4 text-center">
                Von U8 bis U12 - wir fördern den Nachwuchs mit altersgerechtem Training und viel Spaß.
              </p>
              <ul className="text-left text-gray-600 space-y-2">
                <li>• Training:</li>
                <li>-→ Donnerstag: 19:00 - 21:00 Uhr</li>
                <li>-→ Sonntag: 10:30 - 12:00 Uhr</li>
                <li>• Altersgruppen: U8, U12</li>
                <li>• Schnuppertraining jederzeit</li>
              </ul>
            </div> */}
          </div>
        </div>
      </section>

      {/* Training Section */}
      <section id="training" className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Training & Facilities</h2>
              <p className="text-xl text-gray-300 mb-8">
                Professionelle Trainingsanlagen und erfahrene Trainer für optimale Entwicklung
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-red-700 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">⚡</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Moderne Trainingsplätze</h4>
                    {/* <p className="text-gray-300">Zwei Rasenplätze mit Flutlicht und kompletter Infrastruktur</p> */}
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-red-700 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">👥</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Erfahrene Trainer</h4>
                    {/* <p className="text-gray-300">Lizenzierte Trainer mit internationaler Erfahrung</p> */}
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-red-700 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">🏋️</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Fitness & Kondition</h4>
                    {/* <p className="text-gray-300">Umfassende Fitness- und Krafttrainingseinheiten</p> */}
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <img 
                src="/training.jpg"
                alt="Rugby Training"
                className="rounded-lg shadow-lg w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section id="news" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Aktuelle News</h2>
            <p className="text-xl text-gray-600">
              Bleib auf dem Laufenden über Spiele, Events und Vereinsnews
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {news.map((article) => (
              <article key={article.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition duration-300">
                {article.image_url ? (
                  <img 
                    src={article.image_url}
                    alt={article.title}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="bg-red-700 h-48 flex items-center justify-center">
                    <div className="text-white text-center">
                      <div className="text-4xl mb-2">🏆</div>
                      <div className="text-lg font-bold">News</div>
                    </div>
                  </div>
                )}
                <div className="p-6">
                  <div className="text-sm text-red-700 font-semibold mb-2">{formatDate(article.date)}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {/* {article.content.substring(0, 100)}... */}
                    {article.content}
                  </p>
                  {/* <button className="text-red-700 font-semibold hover:text-red-800">
                    Weiterlesen →
                  </button> */}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section id="events" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Kommende Events</h2>
            <p className="text-xl text-gray-600">
              Verpasse keine wichtigen Termine, Spiele und Turniere
            </p>
          </div>
          
          <div className="space-y-6">
            {events.slice(0, 10).map((event) => (
              <div key={event.id} className="bg-gray-50 rounded-lg p-6 flex items-center justify-between hover:shadow-lg transition duration-300">
                <div className="flex items-center space-x-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-700">
                      {new Date(event.date).getDate()}
                    </div>
                    <div className="text-sm text-gray-600">
                      {new Date(event.date).toLocaleDateString('de-DE', { month: 'short' })}
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-900">{event.title}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        event.event_type === 'match' ? 'bg-red-100 text-red-700' :
                        event.event_type === 'training' ? 'bg-blue-100 text-blue-700' :
                        event.event_type === 'tournament' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {event.event_type === 'match' ? 'Spiel' :
                         event.event_type === 'training' ? 'Training' :
                         event.event_type === 'tournament' ? 'Turnier' : 'Versammlung'}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 mb-1">{formatEventDate(event.date)}</p>
                    <p className="text-gray-600 mb-1">{event.location}</p>
                    {event.opponent && (
                      <p className="text-gray-600 mb-1">
                        vs {event.opponent} {event.is_home ? '(Heim)' : '(Auswärts)'}
                      </p>
                    )}
                    <p className="text-gray-700">{event.description}</p>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-sm text-gray-500">
                    {new Date(event.date).toLocaleDateString('de-DE', { weekday: 'long' })}
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {new Date(event.date).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Kontakt & Anfahrt</h2>
            <p className="text-xl text-gray-300">
              Besuche uns oder nimm Kontakt auf - wir freuen uns auf dich!
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold mb-6">Kontaktinformationen</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-red-700 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">📍</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Adresse</h4>
                    <p className="text-gray-300">
                      TV 1861 Ingolstadt<br />
                      Richard-Wagner-Straße 65<br />
                      85057 Ingolstadt
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-red-700 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">📧</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">E-Mail</h4>
                    <p className="text-gray-300">info@rugby-ingolstadt.de</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-red-700 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">📱</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Telefon</h4>
                    <p className="text-gray-300">+49 176 2388 6633</p>
                  </div>
                </div>
              </div>

              {/* Zeigen wir erstmal nicht zweimal an */}
              {/* <div className="mt-8">
                <h4 className="text-lg font-semibold mb-4">Trainingszeiten</h4>
                <div className="space-y-2 text-gray-300">
                  <div>Herren: Dienstag & Donnerstag 19:00 - 21:00</div>
                  <div>Jugend: Donnerstag 18:00 - 19:00 & wSonntag 10:30 - 12:00</div>
                </div>
              </div> */}
            </div>
            
            {/* <div>
              <h3 className="text-2xl font-bold mb-6">Schnuppertraining</h3>
              <p className="text-gray-300 mb-6">
                Interessiert an Rugby? Komm einfach zum Training vorbei oder melde dich vorher bei uns!
              </p>
              
              <form className="space-y-4">
                <div>
                  <input 
                    type="text" 
                    placeholder="Name" 
                    className="w-full px-4 py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:border-red-700"
                  />
                </div>
                <div>
                  <input 
                    type="email" 
                    placeholder="E-Mail" 
                    className="w-full px-4 py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:border-red-700"
                  />
                </div>
                <div>
                  <textarea 
                    placeholder="Nachricht" 
                    rows={4}
                    className="w-full px-4 py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:border-red-700"
                  ></textarea>
                </div>
                <button 
                  type="submit"
                  className="w-full bg-red-700 text-white py-3 rounded-lg hover:bg-red-800 transition duration-300"
                >
                  Nachricht senden
                </button>
              </form>
            </div> */}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            {/* <div>
              <h3 className="text-xl font-bold mb-4">Ingolstadt Baboons</h3>
              <p className="text-gray-400">
                Dein Rugby Club im Herzen Ingolst seit 1999.
              </p>
            </div> */}
            
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <div className="grid grid-cols-2 gap-4">
                <ul className="space-y-2 text-gray-400">
                  <li><a href="#home" className="hover:text-white">Home</a></li>
                  <li><a href="#about" className="hover:text-white">Über Uns</a></li>
                  <li><a href="#teams" className="hover:text-white">Teams</a></li>
                  <li><a href="#training" className="hover:text-white">Training</a></li>
                  <li><a href="#news" className="hover:text-white">News</a></li>
                </ul>
                <ul className="space-y-2 text-gray-400">
                  <li><a href="#events" className="hover:text-white">Events</a></li>
                  <li><a href="#contact" className="hover:text-white">Kontakt</a></li>
                  <li><a href="/impressum.html" className="hover:text-white">Impressum</a></li>
                  <li><a onClick={() => setShowAdminLogin(true)} className="hover:text-white">Admin</a></li>
                </ul>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Kontakt</h4>
              <ul className="space-y-2 text-gray-400">
                <li>TV 1861 Ingolstadt</li>
                <li>Richard-Wagner-Straße 65</li>
                <li>85057 Ingolstadt</li>
                <li>info@rugby-ingolstadt.de</li>
                <li>+49 176 2388 6633</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Social Media</h4>
              <div className="flex space-x-4">
                {/* < href="#" className="text-gray-400 hover:text-white">Facebook</a> */}
                <a href="https://www.instagram.com/rugbyingolstadt?igsh=YnljcmlwajRncTFk" className="text-gray-400 hover:text-white">Instagram</a>
                {/* <a href="#" className="text-gray-400 hover:text-white">Twitter</a> */}
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Ingolstadt Baboons. Alle Rechte vorbehalten.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;